import { useState } from 'react'
import type { ReactNode } from 'react'

import { api } from '../api/client'
import type { ConfigRevision, ConfigStats } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { CategoryBars, CountSeries } from './charts'
import { ErrorBanner, formatDateTime, Spinner } from './common'
import { Confronto, Intera } from './ConfigViste'
import { DataTable } from './DataTable'

/**
 * La storia della configurazione di un apparato, dentro la sua scheda Backup:
 * statistiche delle modifiche, due grafici, gli argomenti che cambiano più
 * spesso e le versioni, ciascuna con il confronto con la precedente e il testo
 * intero — senza rimandare alla pagina «Configurazioni apparati».
 *
 * Una revisione nasce solo quando la configurazione cambia, quindi «versioni»
 * e «modifiche» sono la stessa cosa (meno il primo salvataggio). I numeri
 * vengono dal Control Plane (`GET /device-configs/{ci}/stats`), sulle ultime
 * 120 versioni.
 */
export function StoriaConfigurazione({ ciId, nome }: { ciId: string; nome: string }) {
  const stats = useAsync(() => api.configStats(ciId), [ciId])
  const [aperta, setAperta] = useState<{ id: string; vista: 'confronto' | 'testo' } | null>(null)

  if (stats.error) return <ErrorBanner error={stats.error} />
  if (stats.loading && !stats.data) return <Spinner />
  const s = stats.data
  if (!s || s.changes.length === 0) return null

  const numero = (n: number | null | undefined) => (n ?? 0).toLocaleString(localeCorrente())
  const revisione = (c: ConfigStats['changes'][number]): ConfigRevision => ({
    id: c.id,
    ci_id: ciId,
    transport: c.transport,
    family: '',
    collected_at: c.collected_at,
    job_id: null,
    lines_removed: 0,
    secrets_masked: 0,
    size: c.size,
  })

  return (
    <div className="storia-conf">
      <div className="storia-conf__numeri">
        <Numero etichetta={t('Versioni')} valore={numero(s.total_revisions)} />
        <Numero etichetta={t('Modifiche negli ultimi 30 giorni')} valore={numero(s.changes_last_30_days)} />
        <Numero
          etichetta={t('Righe cambiate')}
          valore={
            <>
              <span className="storia-conf__piu">+{numero(s.total_added)}</span>{' '}
              <span className="storia-conf__meno">−{numero(s.total_removed)}</span>
            </>
          }
        />
        <Numero
          etichetta={t('Intervallo medio fra due modifiche')}
          valore={s.avg_days_between_changes == null ? '—' : t('{n} giorni', { n: s.avg_days_between_changes })}
        />
        <Numero etichetta={t('Righe della configurazione attuale')} valore={numero(s.current_lines)} />
      </div>

      <div className="storia-conf__grafici">
        <div>
          <h4>{t('Modifiche per mese')}</h4>
          <CountSeries
            points={s.per_month.map((m) => ({ date: m.month, value: m.changes }))}
            emptyHint={t('Nessuna modifica negli ultimi 12 mesi.')}
            label={t('Modifiche per mese')}
          />
          <div className="storia-conf__asse">
            <span>{s.per_month[0]?.month}</span>
            <span>{s.per_month[s.per_month.length - 1]?.month}</span>
          </div>
        </div>
        <div>
          <h4>{t('Righe aggiunte e tolte a ogni modifica')}</h4>
          <RigheCambiate modifiche={[...s.changes].reverse().filter((c) => !c.initial)} />
        </div>
      </div>

      {s.topics.length > 0 && (
        <div className="storia-conf__argomenti">
          <h4>{t('Cosa cambia più spesso')}</h4>
          <CategoryBars
            data={s.topics.map((x) => ({ key: x.topic, label: x.topic, value: x.lines }))}
            format={(n) => t('{n} righe', { n })}
          />
          <p className="field__hint">
            {t('Le prime parole delle righe cambiate (interface, ip route, snmp-server…): dicono di cosa si parla.')}
          </p>
        </div>
      )}

      <h4>{t('Versioni')}</h4>
      <DataTable
        rows={s.changes}
        rowKey={(c) => c.id}
        initialSort={{ key: 'data', dir: 'desc' }}
        paginazione={{ predefinita: 10, opzioni: [10, 25, 50] }}
        expanded={(c) => aperta?.id === c.id}
        expand={(c) =>
          aperta?.id !== c.id ? null : aperta.vista === 'confronto' ? (
            <Confronto revisione={revisione(c)} />
          ) : (
            <Intera revisione={revisione(c)} nome={nome} />
          )
        }
        columns={[
          {
            key: 'data',
            header: t('Salvata il'),
            value: (c) => c.collected_at,
            render: (c) => formatDateTime(c.collected_at),
          },
          {
            key: 'modifica',
            header: t('Modifica'),
            value: (c) => (c.initial ? -1 : c.added + c.removed),
            render: (c) =>
              c.initial ? (
                <span className="muted">{t('primo salvataggio')}</span>
              ) : (
                <>
                  <span className="storia-conf__piu">+{c.added}</span>{' '}
                  <span className="storia-conf__meno">−{c.removed}</span>
                </>
              ),
          },
          { key: 'righe', header: t('Righe'), value: (c) => c.lines },
          { key: 'transport', header: t('Letta via'), value: (c) => c.transport },
          {
            key: 'azioni',
            header: '',
            filter: false,
            sort: false,
            value: () => '',
            render: (c) => (
              <span style={{ whiteSpace: 'nowrap' }}>
                <button
                  type="button"
                  className="link"
                  onClick={() =>
                    setAperta(aperta?.id === c.id && aperta.vista === 'confronto' ? null : { id: c.id, vista: 'confronto' })
                  }
                >
                  {aperta?.id === c.id && aperta.vista === 'confronto' ? t('Chiudi') : t('Cosa è cambiato')}
                </button>{' '}
                <button
                  type="button"
                  className="link"
                  onClick={() =>
                    setAperta(aperta?.id === c.id && aperta.vista === 'testo' ? null : { id: c.id, vista: 'testo' })
                  }
                >
                  {aperta?.id === c.id && aperta.vista === 'testo' ? t('Chiudi') : t('Configurazione')}
                </button>
              </span>
            ),
          },
        ]}
      />
      {(s.window ?? 0) < s.total_revisions && (
        <p className="field__hint">
          {t('Statistiche e versioni sulle ultime {n} di {tot}.', { n: s.window ?? 0, tot: s.total_revisions })}
        </p>
      )}
    </div>
  )
}

function Numero({ etichetta, valore }: { etichetta: string; valore: ReactNode }) {
  return (
    <div className="storia-conf__numero">
      <span className="storia-conf__valore">{valore}</span>
      <span className="storia-conf__etichetta">{etichetta}</span>
    </div>
  )
}

/**
 * Una barra per modifica, in ordine di tempo: sopra le righe aggiunte (verde),
 * sotto le tolte (rosso), con la data e i numeri al passaggio del mouse. Una
 * modifica grande si vede subito accanto a quelle da una riga.
 */
function RigheCambiate({ modifiche }: { modifiche: ConfigStats['changes'] }) {
  if (modifiche.length === 0) {
    return <p className="muted">{t('Una sola versione: ancora nessuna modifica da confrontare.')}</p>
  }
  const W = 100
  const H = 40
  const meta = H / 2
  const max = Math.max(1, ...modifiche.map((m) => Math.max(m.added, m.removed)))
  const passo = W / modifiche.length
  const larg = Math.max(0.5, passo * 0.6)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={120} role="img"
         aria-label={t('Righe aggiunte e tolte a ogni modifica')}>
      <line x1="0" y1={meta} x2={W} y2={meta} stroke="var(--divider)" strokeWidth="0.3" />
      {modifiche.map((m, i) => {
        const x = i * passo + (passo - larg) / 2
        const su = (m.added / max) * (meta - 1)
        const giu = (m.removed / max) * (meta - 1)
        const titolo = `${formatDateTime(m.collected_at)}: +${m.added} −${m.removed}`
        return (
          <g key={m.id}>
            {m.added > 0 && (
              <rect x={x} y={meta - su} width={larg} height={su} fill="var(--ok)" opacity={0.85}>
                <title>{titolo}</title>
              </rect>
            )}
            {m.removed > 0 && (
              <rect x={x} y={meta} width={larg} height={giu} fill="var(--error)" opacity={0.85}>
                <title>{titolo}</title>
              </rect>
            )}
          </g>
        )
      })}
    </svg>
  )
}
